import React, { useState } from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend, Line, ComposedChart } from 'recharts';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';

const STUDENTS = 'active';
const ALL = 'all';

const VolumesChart = ({ filter = ALL }) => {
  const { t } = useTranslation();
  const { stats } = useSelector((state) => state.dashboard);
  const [activeBar, setActiveBar] = useState('all');
  
  const volumeData = stats?.volumeStats || [];

  const filteredData = volumeData
    .map(item => ({
      name: item.month,
      Active: item.active,
      'New Admissions': item.newAdmission,
      Dropouts: item.dropout,
      Teachers: item.teachers,
      Total: item.active + item.newAdmission + item.dropout + item.teachers
    }))
    .filter(item => item.Total > 0);

  const totalActive = filteredData.reduce((sum, item) => sum + item.Active, 0);
  const totalNewAdmissions = filteredData.reduce((sum, item) => sum + item['New Admissions'], 0);
  const totalDropouts = filteredData.reduce((sum, item) => sum + item.Dropouts, 0);
  const totalTeachers = filteredData.reduce((sum, item) => sum + item.Teachers, 0);

  const calculateGrowthTrend = () => {
    if (!filteredData?.length || filteredData?.length < 2) return 0;
    
    const firstMonth = filteredData[0]?.Active;
    const lastMonth = filteredData[filteredData.length - 1]?.Active;
    
    if (firstMonth === 0 || firstMonth === undefined) return 0;
    return ((lastMonth - firstMonth) / firstMonth * 100).toFixed(1);
  };

  const growthPercentage = calculateGrowthTrend();

  // Handle filter change
  const handleBarClick = (dataKey) => {
    setActiveBar(activeBar === dataKey ? 'all' : dataKey);
  };

  // Custom tooltip
  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-white p-4 border border-gray-200 shadow-lg rounded-md">
          <p className="font-bold text-gray-700">{`${label}`}</p>
          {payload.map((entry, index) => (
            <p key={index} style={{ color: entry.color }} className="text-sm flex items-center">
              <span className="w-3 h-3 mr-2 rounded-full" style={{ backgroundColor: entry.color }}></span>
              {`${entry.name}: ${entry.value}`}
            </p>
          ))}
        </div>
      );
    }
    return null;
  };

  return (
    <div className="bg-white shadow-md p-4 rounded-lg">
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-xl font-semibold">{t('Volumes')}</h2>
        <div className="text-sm font-medium text-gray-600">
          {growthPercentage > 0 ? (
            <span className="text-green-600">↑ {growthPercentage}% {t('Growth')}</span>
          ) : growthPercentage < 0 ? (
            <span className="text-red-600">↓ {Math.abs(growthPercentage)}% {t('Decline')}</span>
          ) : (
            <span className="text-gray-600">{t('NoChange')}</span>
          )}
        </div>
      </div>
      
      <div className="flex flex-wrap mb-4 gap-2">
        <button 
          onClick={() => handleBarClick('all')}
          className={`px-3 py-1 rounded-full text-xs font-medium ${activeBar === 'all' ? 'bg-gray-800 text-white' : 'bg-gray-100 text-gray-700'}`}
        >
          {t('All')}
        </button>
        <button 
          onClick={() => handleBarClick('Active')}
          className={`px-3 py-1 rounded-full text-xs font-medium ${activeBar === 'Active' ? 'bg-gradient-to-t from-[#241763] via-[#3c2a8a] to-[#5b4b9a] text-white' : 'bg-gray-100 text-gray-700'}`}
        >
          {t('ActiveStudents')}: {totalActive}
        </button>
        <button 
          onClick={() => handleBarClick('New Admissions')}
          className={`px-3 py-1 rounded-full text-xs font-medium ${activeBar === 'New Admissions' ? 'bg-green-600 text-white' : 'bg-gray-100 text-gray-700'}`}
        >
          {t('NewAdmissions')}: {totalNewAdmissions}
        </button>
        <button 
          onClick={() => handleBarClick('Dropouts')}
          className={`px-3 py-1 rounded-full text-xs font-medium ${activeBar === 'Dropouts' ? 'bg-red-600 text-white' : 'bg-gray-100 text-gray-700'}`}
        >
          {t('Dropouts')}: {totalDropouts}
        </button>
        <button 
          onClick={() => handleBarClick('Teachers')}
          className={`px-3 py-1 rounded-full text-xs font-medium ${activeBar === 'Teachers' ? 'bg-amber-600 text-white' : 'bg-gray-100 text-gray-700'}`}
        >
          {t('teachers')}: {totalTeachers}
        </button>
      </div>
      
      {filteredData.length > 0 ? (
        <ResponsiveContainer width="100%" height={350}>
          <ComposedChart 
            data={filteredData}
            margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
            barGap={8}
            barSize={20}
          >
            <defs>
              <linearGradient id="activeGradient" x1="0%" y1="0%" x2="0%" y2="100%">
                <stop offset="0%" stopColor="#5b4b9a" />
                <stop offset="50%" stopColor="#3c2a8a" />
                <stop offset="100%" stopColor="#241763" />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" vertical={false} />
            <XAxis 
              dataKey="name" 
              axisLine={false}
              tickLine={false}
              tick={{ fill: '#64748b', fontSize: 12 }}
            />
            <YAxis 
              axisLine={false}
              tickLine={false}
              tick={{ fill: '#64748b', fontSize: 12 }}
              width={30}
            />
            <Tooltip content={<CustomTooltip />} />
            <Legend 
              iconType="circle"
              iconSize={8}
              wrapperStyle={{ paddingTop: 10 }}
            />
            {(activeBar === 'all' || activeBar === 'Active') && (
              <Bar 
                dataKey="Active" 
                name={t('ActiveStudents')}
                fill="url(#activeGradient)" 
                radius={[4, 4, 0, 0]}
                animationDuration={1500}
                isAnimationActive={true}
              />
            )}
            {(activeBar === 'all' || activeBar === 'New Admissions') && (
              <Bar 
                dataKey="New Admissions" 
                name={t('NewAdmissions')}
                fill="#10b981" 
                radius={[4, 4, 0, 0]}
                animationDuration={1500}
                isAnimationActive={true}
              />
            )}
            {(activeBar === 'all' || activeBar === 'Dropouts') && (
              <Bar 
                dataKey="Dropouts" 
                name={t('Dropouts')}
                fill="#ef4444" 
                radius={[4, 4, 0, 0]}
                animationDuration={1500}
                isAnimationActive={true}
              />
            )}
            {(activeBar === 'all' || activeBar === 'Teachers') && (
              <Bar 
                dataKey="Teachers" 
                name={t('teachers')}
                fill="#f59e0b" 
                radius={[4, 4, 0, 0]}
                animationDuration={1500}
                isAnimationActive={true}
              />
            )}
            {/* Line showing trend for active students */}
            {activeBar === 'Active' && (
              <Line 
                type="monotone" 
                dataKey="Active" 
                stroke="#4f46e5" 
                strokeWidth={2} 
                dot={{ r: 4, strokeWidth: 2 }}
                activeDot={{ r: 6 }}
              />
            )}
          </ComposedChart>
        </ResponsiveContainer>
      ) : (
        <div className="h-64 flex items-center justify-center text-gray-500">
          {t('NoVolumeData')}
        </div>
      )}
    </div>
  );
};

export default VolumesChart;